import React, { useRef } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography
} from '@mui/material';
import { CloudUpload, DeleteOutline, Download, Visibility } from '@mui/icons-material';
import {
  downloadApplicationAttachment,
  downloadPendingFile,
  viewApplicationAttachment
} from '../../utils/authorizationAttachments';

/**
 * Shared add / view / download / delete UI for Authorization application attachments.
 * Supports saved server attachments and pending local File objects (new application).
 * View/Download always available for saved files (defaults used when parent omits handlers).
 */
function AuthorizationAttachmentsSection({
  title = 'Attachments',
  attachments = [],
  pendingFiles = [],
  readOnly = false,
  saving = false,
  accept = '.pdf,.png,.jpg,.jpeg',
  onAddFiles,
  onRemovePending,
  onView,
  onDownload,
  onDelete
}) {
  const fileInputRef = useRef(null);
  const saved = Array.isArray(attachments) ? attachments : [];
  const pending = Array.isArray(pendingFiles) ? pendingFiles : [];
  const empty = saved.length === 0 && pending.length === 0;

  const handleView = (att) => {
    if (onView) onView(att);
    else viewApplicationAttachment(att);
  };

  const handleDownload = (att) => {
    if (onDownload) onDownload(att);
    else downloadApplicationAttachment(att);
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {!readOnly && onAddFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={accept}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) onAddFiles(files);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={saving}
              onClick={() => fileInputRef.current?.click()}
            >
              Add attachment
            </Button>
          </>
        )}
      </Stack>

      {empty ? (
        <Typography variant="body2" color="text.secondary">
          No attachments.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {saved.map((att) => (
            <Stack
              key={att._id || att.fileUrl}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" noWrap sx={{ flex: 1 }} title={att.fileName || 'Document'}>
                {att.fileName || 'Document'}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {att.fileUrl && (
                  <>
                    <Button size="small" startIcon={<Visibility />} onClick={() => handleView(att)}>
                      View
                    </Button>
                    <Button size="small" startIcon={<Download />} onClick={() => handleDownload(att)}>
                      Download
                    </Button>
                  </>
                )}
                {!readOnly && onDelete && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={saving}
                    onClick={() => onDelete(att)}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            </Stack>
          ))}

          {pending.map((file, index) => (
            <Stack
              key={`pending-${index}-${file.name}-${file.size}`}
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" noWrap sx={{ flex: 1 }} title={file.name}>
                {file.name}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (pending)
                </Typography>
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Button size="small" startIcon={<Download />} onClick={() => downloadPendingFile(file)}>
                  Download
                </Button>
                {!readOnly && onRemovePending && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutline />}
                    disabled={saving}
                    onClick={() => onRemovePending(index)}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Allowed: PDF, JPG, PNG · max 15 MB each
      </Typography>
    </Box>
  );
}

export default AuthorizationAttachmentsSection;
